<template>
  <!--
    THE TACTICAL MAP: the Frontier's island flat, in SVG, as a toggle
    beside the 3D one. Matt, 2026-09-22: "maybe we ought to offer a
    tactical version of the map for the frontier in case the 3D scrolling
    around thing is difficult for someone to follow... it would have to
    have all the information you need, though, with tappable hexes to get
    more information."

    It draws from the SAME layout the 3D scene draws from (frontierLayout),
    so nothing can be on one map and not the other: terrain, fog, the gold
    you can step to, the city's edge, villages, camps, raiders, finds,
    other peoples' ground in their colour, and your units by kind. Tap a
    gold hex to step, a camp or people to pick a fight, a unit to take it in
    hand - and ANY other hex to be told what it is (hold works too, the
    way it does on the 3D map). Rotated thirty degrees so up is up on the
    TV as well.
  -->
  <div ref="wrap" class="tactical">
    <svg
      class="tactical__svg"
      :viewBox="viewBox"
      @pointerdown="onDown"
      @pointermove="onMove"
      @pointerup="onUp"
      @pointercancel="onCancel"
      @wheel.prevent="onWheel"
      @contextmenu.prevent
    >
      <!-- Drawn marks, not type: a font's ♣ and 👁 looked like text on a
           spreadsheet next to the 3D board (polish pass, 2026-09-26). Each
           symbol is drawn in a 20x20 box centred on 0,0. -->
      <defs>
        <symbol id="ts-tree" viewBox="-10 -10 20 20"><path d="M-5 3 L-2 -5 L1 3 Z M0 4 L4 -7 L8 4 Z" /><path d="M-2 3v3M4 4v3" fill="none" stroke="var(--ts-stem, #4a3520)" stroke-width="1.4" /></symbol>
        <symbol id="ts-peak" viewBox="-10 -10 20 20"><path d="M-9 6 L-1 -7 L3 -1 L5 -4 L10 6 Z" /><path d="M-3.5 -3 L-1 -7 L1.4 -3.4 L0 -2.4 L-1.4 -3.6 Z" fill="#f4f4f4" stroke="none" /></symbol>
        <symbol id="ts-hill" viewBox="-10 -10 20 20"><path d="M-9 5 Q-4 -5 1 5 Z M-1 5 Q4 -2 9 5 Z" /></symbol>
        <symbol id="ts-rocks" viewBox="-10 -10 20 20"><path d="M-7 4 L-5 -1 L-1 -1 L1 4 Z M1 5 L3 0 L7 1 L8 5 Z M-2 -2 L0 -6 L3 -5 L3 -2 Z" /></symbol>
        <symbol id="ts-castle" viewBox="-10 -10 20 20"><path d="M-8 7 V-3 H-6 V-6 H-4 V-3 H-1.5 V-6 H1.5 V-3 H4 V-6 H6 V-3 H8 V7 Z" /><path d="M-1.6 7 V2.5 A1.6 1.6 0 0 1 1.6 2.5 V7 Z" fill="var(--ts-door, #4a3322)" stroke="none" /></symbol>
        <symbol id="ts-house" viewBox="-10 -10 20 20"><path d="M-7 7 V-1 L0 -7 L7 -1 V7 Z" /><path d="M-1.5 7 V2 H1.5 V7 Z" fill="var(--ts-door, #4a3322)" stroke="none" /></symbol>
        <symbol id="ts-tower" viewBox="-10 -10 20 20"><path d="M-4 8 L-3 -4 H-5 V-8 H5 V-4 H3 L4 8 Z" /></symbol>
        <symbol id="ts-tent" viewBox="-10 -10 20 20"><path d="M-9 7 L0 -8 L9 7 Z" /><path d="M0 -8 L-3 7 H3 Z" fill="var(--ts-door, #4a3322)" stroke="none" /></symbol>
        <symbol id="ts-find" viewBox="-10 -10 20 20"><path d="M0 -9 L2.2 -2.2 L9 0 L2.2 2.2 L0 9 L-2.2 2.2 L-9 0 L-2.2 -2.2 Z" /></symbol>
        <symbol id="ts-sword" viewBox="-10 -10 20 20"><path d="M-6 6 L5 -5 M-6 -5 L5 6 M-7 3 L-3 7 M3 7 L7 3" fill="none" stroke="currentColor" stroke-width="var(--ts-line, 1.8)" stroke-linecap="round" /></symbol>
        <symbol id="ts-eye" viewBox="-10 -10 20 20"><path d="M-8 0 Q0 -7 8 0 Q0 7 -8 0 Z" fill="none" stroke="currentColor" stroke-width="var(--ts-line, 1.8)" stroke-linecap="round" /><circle r="2.4" /></symbol>
        <symbol id="ts-flag" viewBox="-10 -10 20 20"><path d="M-4 8 V-8" fill="none" stroke="currentColor" stroke-width="var(--ts-line, 1.8)" stroke-linecap="round" /><path d="M-4 -8 L6 -5 L-4 -2 Z" /></symbol>
      </defs>
      <!-- The tiles' thickness, like the 3D board's: a darker copy of every
           known hex dropped a little, drawn under all of them. -->
      <polygon v-for="t in hexes.filter((h) => !h.fog)" :key="`side${t.id}`" :points="t.points" class="thex__side" :class="`thex--${t.model}`" :transform="`translate(0 ${size * 0.16})`" />
      <g v-for="t in hexes" :key="t.id" :data-q="t.q" :data-r="t.r" class="thex" :class="[`thex--${t.model}`, `thex--v${t.shade}`, { 'thex--fog': t.fog, 'thex--target': t.target, 'thex--here': t.here, 'thex--city': t.city }]">
        <polygon :points="t.points" class="thex__ground" :style="t.rival ? { '--people': t.rivalColour } : null" />
        <polygon v-if="t.rival" :points="t.inner" class="thex__people" :style="{ '--people': t.rivalColour }" />
        <polygon v-if="t.rival" :points="t.inner" class="thex__peopleedge" :style="{ '--people': t.rivalColour }" />
        <polygon v-if="t.city" :points="t.inner" class="thex__cityband" />
        <polygon v-if="t.target" :points="t.inner" class="thex__gold" />
        <polygon v-if="t.here" :points="t.inner" class="thex__here" />
        <text v-if="showYields && t.makes" :x="t.x" :y="t.y + size * 0.62" text-anchor="middle" class="thex__makes" :class="`thex__makes--${t.makes}`">{{ t.makes }}</text>
        <use v-if="t.glyph" :href="`#ts-${t.glyph}`" :x="t.x - size * 0.42" :y="t.y - size * 0.42" :width="size * 0.84" :height="size * 0.84" class="thex__glyph" :class="`thex__glyph--${t.glyph}`" />
      </g>

      <!-- roads (DESIGN.md "ROADS"): centre to centre, under everything else -->
      <line v-for="rd in roadLines" :key="rd.id" :x1="rd.x1" :y1="rd.y1" :x2="rd.x2" :y2="rd.y2" class="troad" />
      <!-- things on the ground: the town, villages, finds, camps, seats -->
      <g v-for="f in marks" :key="f.id" class="tmark" :class="[`tmark--${f.kind}`, { 'tmark--taken': f.kind === 'village' && f.colour }]" :style="f.colour ? { '--people': f.colour } : null" :data-q="f.q" :data-r="f.r">
        <use v-if="f.symbol" :href="`#ts-${f.symbol}`" :x="f.x - f.w / 2" :y="f.y - f.w / 2" :width="f.w" :height="f.w" class="tmark__sym" />
        <!-- a camp's strength, in the same pips as the pieces carry -->
        <circle v-for="k in f.pips || 0" :key="`p${k}`" :cx="f.x - ((f.pips - 1) * size * 0.2) / 2 + (k - 1) * size * 0.2" :cy="f.y + size * 0.62" :r="size * 0.085" class="tmark__pip" />
        <text v-if="f.kind === 'village' && f.damaged" :x="f.x" :y="f.y + 4" class="tmark__x" text-anchor="middle">×</text>
      </g>

      <!-- pieces (2026-09-25 rework): your units a solid token in the role's red
           with one pip per strength; raiders a red-ringed dark token. No text. -->
      <!-- A fight: a ring bursts out of the hex (pacing pass, 2026-09-28). -->
      <circle v-if="burst" :key="burst.key" :cx="centre(burst.q, burst.r).x" :cy="centre(burst.q, burst.r).y" :r="size * 0.5" class="tburst" />
      <!-- The group carries the position as a transform, so a step SLIDES
           from hex to hex instead of jumping (pacing pass, 2026-09-28). -->
      <g v-for="p in pieces" :key="p.id" class="tpiece" :class="[`tpiece--${p.kind}`, { 'tpiece--foe': p.foe, 'tpiece--chosen': p.chosen }]" :style="{ transform: `translate(${p.x}px, ${p.y}px)` }" :data-q="p.q" :data-r="p.r">
        <circle v-if="p.chosen" :r="size * 0.62" class="tpiece__ring" />
        <circle :r="size * 0.44" class="tpiece__body" />
        <use :href="`#ts-${p.icon}`" :x="-size * 0.3" :y="-size * 0.3" :width="size * 0.6" :height="size * 0.6" class="tpiece__kind" />
        <circle v-for="k in p.pips" :key="k" :cx="-((p.pips - 1) * size * 0.2) / 2 + (k - 1) * size * 0.2" :cy="size * 0.6" :r="size * 0.085" class="tpiece__pip" />
      </g>

    </svg>
    <button v-if="moved" class="tactical__reset" @click="resetView">Reset view</button>
  </div>
</template>

<script>
import { rivalColour } from '@/engine/rivals';

// Pointy-top axial hexes, turned thirty degrees so the board sits the way
// the TV's island does. All geometry is worked out here in pixels so the
// text can stay upright.
const SQRT3 = Math.sqrt(3);
const TURN = -Math.PI / 6;
const rot = (x, y) => [x * Math.cos(TURN) - y * Math.sin(TURN), x * Math.sin(TURN) + y * Math.cos(TURN)];
const GLYPH = {
  'grass-forest': 'tree', 'grass-hill': 'hill', 'stone-hill': 'hill', 'stone-mountain': 'peak', 'stone-rocks': 'rocks', 'sand-rocks': 'rocks', 'water-rocks': 'rocks', 'water-island': 'hill',
};
const DRAG_PX = 10;

export default {
  name: 'TacticalMap',
  props: {
    // A fight just happened here: { q, r, key }.
    burst: { type: Object, default: null },
    // { tiles, features } from layoutFrontier
    map: { type: Object, required: true },
    // the labels the board hangs over the 3D map: { id, kind, text, colour }
    labels: { type: Array, default: () => [] },
    civ: { type: Object, required: true },
    // Label every seen hex with what a day's work there makes (playtest 11).
    showYields: { type: Boolean, default: false },
  },
  emits: ['pick', 'inspect'],
  // Pan and zoom (playtest 7: "on the flat version, I can't pan around or
  // zoom in, which I should be able to do"). One finger drags, two pinch,
  // a wheel zooms; the view is a zoom `k` and an offset in SVG units.
  data() { return { size: 16, k: 1, vx: 0, vy: 0 }; },
  computed: {
    centre() {
      return (q, r) => {
        const [x, y] = rot(this.size * SQRT3 * (q + r / 2), this.size * 1.5 * r);
        return { x, y };
      };
    },
    rivalColourOf() {
      const by = {};
      (this.civ.rivals || []).forEach((rv) => { by[rv.id] = rivalColour(rv); });
      return (id) => by[id] || '#888';
    },
    hexes() {
      return (this.map.tiles || []).map((t) => {
        const { x, y } = this.centre(t.q, t.r);
        const corner = (k, s) => {
          const a = (Math.PI / 180) * (60 * k - 30) + TURN;
          return `${(x + s * Math.cos(a)).toFixed(1)},${(y + s * Math.sin(a)).toFixed(1)}`;
        };
        const points = [0, 1, 2, 3, 4, 5].map((k) => corner(k, this.size)).join(' ');
        const inner = [0, 1, 2, 3, 4, 5].map((k) => corner(k, this.size * 0.82)).join(' ');
        return {
          ...t, x, y, points, inner,
          glyph: t.fog ? '' : (GLYPH[t.model] || ''),
          // A little variation between neighbours, fixed per hex, so a
          // field of grass is ground and not a spreadsheet.
          shade: (((t.q * 7 + t.r * 13) % 3) + 3) % 3,
          rivalColour: t.rival ? this.rivalColourOf(t.rival) : null,
        };
      });
    },
    roadLines() {
      return (this.map.roads || []).map((sg) => {
        const a = this.centre(sg.a.q, sg.a.r); const b0 = this.centre(sg.b.q, sg.b.r);
        const b = sg.half ? { x: (a.x + b0.x) / 2, y: (a.y + b0.y) / 2 } : b0;
        return { id: sg.id, x1: a.x, y1: a.y, x2: b.x, y2: b.y };
      });
    },
    marks() {
      const out = [];
      const s = this.size;
      (this.map.features || []).forEach((f) => {
        const { x, y } = this.centre(f.q, f.r);
        // A building shares its hex with whoever stands there, so it moves
        // up and left and the pieces down and right (the town used to be
        // hidden under the unit mustered on it).
        const shared = this.occupied.has(`${f.q},${f.r}`);
        const bx = shared ? x - s * 0.3 : x; const by = shared ? y - s * 0.28 : y;
        if (f.id === 'town') out.push({ id: f.id, kind: 'town', symbol: 'castle', q: f.q, r: f.r, x: bx, y: by, w: s * 1.05 });
        else if (f.id.startsWith('village:')) out.push({ id: f.id, kind: 'village', symbol: 'house', q: f.q, r: f.r, x: bx, y: by, w: s * 0.8, damaged: f.damaged, colour: f.colour });
        else if (f.id.startsWith('menace:')) {
          const m = (this.civ.menaces || []).find((x2) => `menace:${x2.id}` === f.id);
          out.push({ id: f.id, kind: 'camp', symbol: 'tent', q: f.q, r: f.r, x, y: y - s * 0.1, w: s * 0.85, pips: Math.max(1, Math.min(6, m?.strength || 1)) });
        }
        else if (f.id.startsWith('find:')) out.push({ id: f.id, kind: 'find', symbol: 'find', q: f.q, r: f.r, x: bx, y: by, w: s * 0.7 });
        // A found region's landmark (a cabin, a mine, a watchtower), drawn
        // small and neutral so the flat map shows what the 3D one does.
        else if (f.id.startsWith('region:')) {
          const m = f.model || '';
          const symbol = m.includes('tree') ? 'tree' : m.includes('tower') ? 'tower' : m.includes('bridge') ? null : 'house';
          if (symbol) out.push({ id: f.id, kind: 'landmark', symbol, q: f.q, r: f.r, x: bx, y: by, w: s * 0.7 });
        } else if (f.id.startsWith('rival:') && f.id.endsWith(':seat')) out.push({ id: f.id, kind: 'seat', symbol: 'tower', q: f.q, r: f.r, x: bx, y: by, w: s * 0.95, colour: f.colour });
      });
      // A people you have met is drawn where it lives even if the layout
      // gave it no seat feature (playtest 7: "the red people are listed...
      // but I don't actually see where they are on the map").
      const onMap = new Set(this.hexes.map((h) => `${h.q},${h.r}`));
      (this.civ.rivals || []).forEach((rv) => {
        if (!rv.met || rv.q === undefined || !onMap.has(`${rv.q},${rv.r}`)) return;
        if (out.some((m) => m.kind === 'seat' && m.q === rv.q && m.r === rv.r)) return;
        const { x, y } = this.centre(rv.q, rv.r);
        out.push({ id: `rival:${rv.id}:seat`, kind: 'seat', symbol: 'tower', q: rv.q, r: rv.r, x, y, w: s * 0.95, colour: this.rivalColourOf(rv.id) });
      });
      return out;
    },
    // Hexes where a piece stands: a building there steps aside for it.
    occupied() {
      return new Set((this.map.features || []).filter((f) => f.id.startsWith('unit:') || f.id.startsWith('raider:')).map((f) => `${f.q},${f.r}`));
    },
    // Hexes with a building on them, so a piece there steps aside too.
    built() {
      return new Set((this.map.features || []).filter((f) => f.id === 'town' || f.id.startsWith('village:') || f.id.startsWith('find:') || (f.id.startsWith('rival:') && f.id.endsWith(':seat'))).map((f) => `${f.q},${f.r}`));
    },
    pieces() {
      return (this.map.features || [])
        .filter((f) => f.id.startsWith('unit:') || f.id.startsWith('raider:'))
        .map((f) => {
          const c = this.centre(f.q, f.r);
          const aside = this.built.has(`${f.q},${f.r}`);
          const x = aside ? c.x + this.size * 0.3 : c.x;
          const y = aside ? c.y + this.size * 0.12 : c.y - this.size * 0.08;
          const foe = Boolean(f.foe);
          return {
            id: f.id, q: f.q, r: f.r, x, y,
            kind: f.kind || (foe ? 'raider' : 'warband'), foe, chosen: Boolean(f.chosen),
            icon: foe ? 'sword' : (f.kind === 'scout' ? 'eye' : f.kind === 'pioneer' ? 'flag' : 'sword'),
            pips: Math.max(1, Math.min(6, f.size || 1)),
          };
        });
    },
    // The board's own labels, placed above the thing they name.
    chips() {
      const where = (id) => {
        const [kind, rest] = id.split(':');
        const find = (list, key = 'id') => (list || []).find((x) => String(x[key]) === rest);
        if (kind === 'menace') return find(this.civ.menaces);
        if (kind === 'raider') return find(this.civ.raiders);
        if (kind === 'village') return find(this.civ.villages);
        if (kind === 'rival') return find(this.civ.rivals);
        if (kind === 'unit') return find(this.civ.units);
        return null;
      };
      return this.labels.map((l) => {
        const at = where(l.id);
        if (!at) return null;
        const { x, y } = this.centre(at.q, at.r);
        // Short on the flat map: "Hawks · 3", not "The Hawks (warband of 3)".
        const text = l.text.replace(/^[Tt]he /, '').replace(/ \((?:[\w ]+?)(?: of (\d+))?\)$/, (m, n) => (n ? ` · ${n}` : ''));
        const w = Math.max(16, text.length * 4.6 + 10);
        // Kept inside the map: a people beyond the drawn hexes still gets
        // its chip, at the edge nearest to where it lives.
        const b = this.base;
        const cx = Math.min(b.x + b.w - w / 2 - 2, Math.max(b.x + w / 2 + 2, x));
        const cy = Math.min(b.y + b.h - 10, Math.max(b.y + 10, y - this.size * 0.95));
        return { ...l, text, x: cx, y: cy, w };
      }).filter(Boolean);
    },
    // Cropped to the ground you have SEEN, with a thin rim of the unknown,
    // so the hexes are as big as the width allows (2026-09-25: "takes up
    // way too much vertical space").
    base() {
      const known = this.hexes.filter((h) => !h.fog);
      const use = known.length ? known : this.hexes;
      const xs = use.map((h) => h.x);
      const ys = use.map((h) => h.y);
      if (!xs.length) return { x: 0, y: 0, w: 100, h: 100 };
      const pad = this.size * 1.15;
      const x0 = Math.min(...xs) - pad; const x1 = Math.max(...xs) + pad;
      const y0 = Math.min(...ys) - pad; const y1 = Math.max(...ys) + pad;
      return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    },
    viewBox() {
      const b = this.base;
      const w = b.w / this.k; const h = b.h / this.k;
      const x = b.x + (b.w - w) / 2 + this.vx; const y = b.y + (b.h - h) / 2 + this.vy;
      return `${x.toFixed(1)} ${y.toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)}`;
    },
    moved() { return this.k !== 1 || this.vx !== 0 || this.vy !== 0; },
  },
  created() { this.down = null; this.hold = null; this.held = false; this.fingers = new Map(); this.pinch = null; },
  beforeUnmount() { this.clearHold(); },
  methods: {
    // The 3D scene's two camera calls, answered on the flat map too.
    lookAt(q, r) {
      if (this.k === 1) return;
      const { x, y } = this.centre(q, r);
      const b = this.base;
      this.vx = x - (b.x + b.w / 2);
      this.vy = y - (b.y + b.h / 2);
      this.clampView();
    },
    bringIntoView(q, r) { this.lookAt(q, r); },
    resetView() { this.k = 1; this.vx = 0; this.vy = 0; },
    // SVG units per screen pixel at the current zoom.
    unitsPerPx() {
      const rect = this.$refs.wrap.getBoundingClientRect();
      return rect.width ? (this.base.w / this.k) / rect.width : 1;
    },
    clampView() {
      this.k = Math.min(4, Math.max(1, this.k));
      const b = this.base;
      const mx = (b.w - b.w / this.k) / 2; const my = (b.h - b.h / this.k) / 2;
      this.vx = Math.min(mx, Math.max(-mx, this.vx));
      this.vy = Math.min(my, Math.max(-my, this.vy));
    },
    onWheel(e) {
      this.k *= Math.exp(-e.deltaY * 0.002);
      this.clampView();
    },
    hexAt(e) {
      const el = e.target?.closest?.('[data-q]');
      if (!el) return null;
      return { q: Number(el.dataset.q), r: Number(el.dataset.r) };
    },
    onDown(e) {
      this.fingers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.fingers.size === 2) {
        const [a, b] = [...this.fingers.values()];
        this.pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, k: this.k };
        this.clearHold();
        this.down = null;
        return;
      }
      this.down = { x: e.clientX, y: e.clientY, hex: this.hexAt(e), vx: this.vx, vy: this.vy };
      this.held = false;
      this.clearHold();
      this.hold = window.setTimeout(() => { this.held = true; this.tell(this.down?.hex, e); }, 500);
    },
    onMove(e) {
      if (this.fingers.has(e.pointerId)) this.fingers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pinch && this.fingers.size === 2) {
        const [a, b] = [...this.fingers.values()];
        this.k = this.pinch.k * (Math.hypot(a.x - b.x, a.y - b.y) / this.pinch.d);
        this.clampView();
        return;
      }
      if (!this.down) return;
      const dx = e.clientX - this.down.x; const dy = e.clientY - this.down.y;
      if (Math.hypot(dx, dy) > DRAG_PX) {
        this.clearHold();
        this.down.dragging = true;
        if (this.k > 1) {
          const u = this.unitsPerPx();
          this.vx = this.down.vx - dx * u;
          this.vy = this.down.vy - dy * u;
          this.clampView();
        }
      }
    },
    onUp(e) {
      this.fingers.delete(e.pointerId);
      if (this.pinch) { if (this.fingers.size < 2) this.pinch = null; this.down = null; return; }
      this.clearHold();
      if (this.down && !this.held && Math.hypot(e.clientX - this.down.x, e.clientY - this.down.y) <= DRAG_PX) this.tap(this.down.hex, e);
      this.down = null;
    },
    onCancel(e) { this.fingers.delete(e?.pointerId); this.pinch = null; this.clearHold(); this.down = null; },
    clearHold() { if (this.hold) { window.clearTimeout(this.hold); this.hold = null; } },
    tell(hex, e) {
      if (!hex) return;
      const r = this.$refs.wrap.getBoundingClientRect();
      this.$emit('inspect', { ...hex, atX: e.clientX - r.left, atY: e.clientY - r.top });
    },
    // A tap does the one thing the hex is for, or asks what it is.
    tap(hex, e) {
      if (!hex) return;
      const t = (this.map.tiles || []).find((x) => x.q === hex.q && x.r === hex.r);
      if (!t || t.fog) { this.tell(hex, e); return; }
      const unit = (this.civ.units || []).find((u) => u.q === hex.q && u.r === hex.r);
      if (t.target) { this.$emit('pick', { kind: 'march', q: hex.q, r: hex.r }); return; }
      if (unit && !t.menace && !t.raider) { this.$emit('pick', { kind: 'unit', id: unit.id }); return; }
      const r = this.$refs.wrap.getBoundingClientRect();
      const atX = e.clientX - r.left;
      if (t.menace) { this.$emit('pick', { kind: 'menace', id: t.menace, q: hex.q, r: hex.r, atX }); return; }
      if (t.raider) { this.$emit('pick', { kind: 'raider', id: t.raider, q: hex.q, r: hex.r, atX }); return; }
      if (t.rival) { this.$emit('pick', { kind: 'rival', id: t.rival, q: hex.q, r: hex.r, atX }); return; }
      this.tell(hex, e);
    },
  },
};
</script>
